<template>
  <div
    ref="predictiveSearch"
    class="predictive-search container grid"
  >
    <button
      class="predictive-search__close icon-button"
      type="button"
      @click.prevent="handleCloseClick"
    >
      <span
        class="visually-hidden"
        v-text="$string('accessibility.close')"
      />

      <icon-close />
    </button>

    <div
      v-if="query && noProducts"
      class="predictive-search__no-results col xs6 l2-11"
    >
      <p
        class="text-body-xs text-body-s-desktop"
        v-html="$string('search.no_results_html', { replace: { query } })"
      />
    </div>

    <div class="predictive-search__panel col xs6 l2-4">
      <p
        class="predictive-search__title text-label-s"
        v-text="suggestionsTitle"
      />

      <ul class="predictive-search__suggestions">
        <li
          v-for="(suggestion, index) of suggestions"
          :key="`${suggestion.handle}-${index}`"
          class="predictive-search__suggestion-item"
        >
          <a
            class="
              predictive-search__suggestion
              text-body-xs
              text-body-s-desktop
            "
            :href="suggestion.url"
            v-html="suggestion.title"
          />
        </li>
      </ul>
    </div>

    <div
      v-if="products.length"
      class="
        predictive-search__panel
        predictive-search__products-panel
        col xs6 l5-11
      "
    >
      <p
        class="predictive-search__title text-label-s"
        v-text="productsTitle"
      />

      <div
        ref="products"
        class="predictive-search__products-container"
      >
        <div class="predictive-search__products">
          <product-card
            v-for="(product, index) of products"
            :key="`product-${product.id}-${index}`"
            block-class="predictive-search"
            delay-click
            :index="index"
            :product="product"
            tiny
            @view-product="(url) => handleProductClick(product.id, url)"
          />
        </div>
      </div>

      <div
        v-if="response.products?.length"
        class="predictive-search__view-all"
      >
        <btn
          :label="viewAllLabel"
          label-class="text-button-xs"
          modifiers="text"
          :show-icon="false"
          :url="viewAllUrl"
        />
      </div>
    </div>

    <boost-api-call
      endpoint="search/suggest"
      event-type="suggest"
      :limit="4"
      :query="query"
      :shop="$variable('store.localisedPermanentDomain')"
      @boost-prices-updated="boostPricesUpdated"
      @boost-updated="boostUpdated"
    />

    <boost-analytics
      :analytics-event="analyticsEvent"
      type="search"
      @analytics-submitted="handleAnalyticsSubmitted"
    />
  </div>
</template>

<script>

/**
 * Vue: Predictive search (predictive-search)
 * -----------------------------------------------------------------------------
 * Predictive search results.
 *
 * @param {Boolean} isActive - Search is active.
 * @param {String} query - Current search query.
 * @param {Array} searchBars - Search bar component references.
 * @param {Object} searchSettings - Search settings.
 *
 */
import { nextTick } from 'vue'
import { mapActions } from 'vuex'

import BoostAnalytics from '~async/boost-analytics/boost-analytics'
import BoostApiCall from '~global/boost-api-call/boost-api-call'
import Btn from '~global/btn/btn'
import ProductCard from '~async/product-card/product-card'

import IconClose from '~icons/misc/close.svg'

import { convertToHandle } from '~/helpers/convert'
import { formatProduct } from '~/helpers/format'
import { formatBoostProduct } from '~/helpers/format-boost'
import productQuery from '~/graphql/queries/product.gql'

export default {
  name: 'PredictiveSearch',

  components: {
    BoostAnalytics,
    BoostApiCall,
    Btn,
    IconClose,
    ProductCard,
  },

  props: {
    isActive: {
      type: Boolean,
      default: true,
    },
    query: {
      type: String,
      default: '',
    },
    searchBars: {
      type: Array,
      default: () => [],
    },
    searchSettings: {
      type: Object,
      default: () => ({
        popularSearches: [],
        trendingProducts: [],
      }),
    },
  },

  emits: {
    'close-search': () => true,
    'update-query': (payload) => typeof payload === 'string',
    'update-redirect': (payload) => typeof payload === 'string',
  },

  data() {
    return {
      analyticsEvent: {},
      boostSuggestions: [],
      noProducts: false,
      products: [false, false, false, false],
      redirect: '',
      response: {},
      storageKey: 'predictive-search',
      suggestions: [],
      trendingProducts: [],
    }
  },

  computed: {

    /**
     * Compute if suggestions are being shown.
     * @returns {Boolean}
     */
    isSuggestions() {
      return this.response.suggestions?.length
    },

    /**
     * Compute products title.
     * @returns {String}
     */
    productsTitle() {
      return this.response.products?.length
        ? this.$string('search.products')
        : this.$string('search.trending_products')
    },

    /**
     * Compute suggestions title.
     * @returns {String}
     */
    suggestionsTitle() {
      return this.isSuggestions
        ? this.$string('search.suggestions')
        : this.$string('search.popular_searches')
    },

    /**
     * Compute total results.
     * @returns {Number}
     */
    totalResults() {
      let totalResults = 0

      if (this.response.total_product) {
        totalResults += this.response.total_product
      }

      if (this.response.collections) {
        totalResults += this.response.collections.length
      }

      if (this.response.pages) {
        totalResults += this.response.pages.length
      }

      return totalResults
    },

    /**
     * Compute view all link URL.
     * @returns {String}
     */
    viewAllUrl() {
      return `${this.$variable('routes.search')}?q=${this.query}`
    },

    /**
     * Compute view all label.
     * @returns {String}
     */
    viewAllLabel() {
      return this.$string('search.view_all', {
        pluralise: this.totalResults,
        replace: { count: this.totalResults },
      })
    },
  },

  watch: {

    /**
     * Watch isActive to trap focus.
     */
    isActive() {
      this.updateFocusTrap()
    },

    /**
     * Watch query to reset to trending products and suggestions.
     */
    query() {
      if (this.query) {
        this.noProducts = false
        return
      }

      this.response = {}
    },

    /**
     * Watch products update to scroll to start of container.
     */
    products() {
      nextTick().then(() => {
        if (!this.$refs.products) {
          return
        }

        this.$refs.products.scrollTo(0, 0)
      })
    },

    /**
     * Watch redirect update to emit.
     */
    redirect() {
      this.$emit('update-redirect', this.redirect)
    },

    /**
     * Watch response data to update products, suggestions, and redirect.
     */
    response() {

      /**
       * Update products.
       */
      if (this.response.products?.length) {
        this.noProducts = false

        this.products = this.response.products
          .map((product) => {
            if (!product) {
              return false
            }

            return formatBoostProduct(product)
          })
          .filter(Boolean)
          .slice(0, 4)

      } else {
        this.noProducts = true
        this.products = this.trendingProducts
      }

      /**
       * Update suggestions.
       */
      if (this.response.suggestions?.length) {
        const formattedQuery = this.query
          .toLowerCase()
          .trim()
          .replaceAll('&', '(?:&|and)')

        const regex = new RegExp(`(?<query>${formattedQuery})`, 'g')

        this.boostSuggestions = this.response.suggestions.map((suggestion) => {
          const title = suggestion.replace(regex, (_, $1) => {
            return `<strong>${$1}</strong>`
          })

          return {
            handle: convertToHandle(suggestion),
            query: suggestion,
            title,
            url: `${cnvs.routes.normalisedRoot}search?q=${encodeURIComponent(suggestion)}`,
          }
        })

        this.suggestions = this.boostSuggestions

      } else {
        this.suggestions = this.searchSettings.popularSearches
      }

      /**
       * Update other.
       */
      this.redirect = this.response.redirect || ''
      this.updateFocusTrap()
    },
  },

  mounted() {
    this.updateFocusTrap()
    this.queryTrendingProducts()
    this.suggestions = this.searchSettings.popularSearches
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      setFocusTrap: 'accessibility/setFocusTrap',
    }),

    /**
     * Query trending products.
     */
    async queryTrendingProducts() {
      if (!this.searchSettings.trendingProducts.length) {
        this.products = []
        return
      }

      if (sessionStorage.getItem(this.storageKey)) {
        this.trendingProducts = JSON.parse(sessionStorage.getItem(this.storageKey))
        this.products = this.trendingProducts
        return
      }

      try {
        this.state = 'loading'

        const queue = this.searchSettings.trendingProducts.map((handle) => {
          return cnvs.Query({
            query: productQuery,
            variables: {
              country: cnvs.store.country,
              handle,
              language: cnvs.store.language,
            },
          })
        })

        const response = await Promise.allSettled(queue)

        this.trendingProducts = response.map((promise) => {
          if (promise.status !== 'fulfilled') {
            return false
          }

          return formatProduct(promise.value)
        }).filter(Boolean)

        sessionStorage.setItem(this.storageKey, JSON.stringify(this.trendingProducts))
        this.products = this.trendingProducts
        this.updateFocusTrap()
        this.state = 'ready'

      } catch (error) {
        this.products = []
        reportError('Failed to load trending products', error)
      }
    },

    /**
     * Callback for Boost API call.
     * @param {Object} response - Response from API call.
     */
    boostUpdated(response) {
      if (!response || !this.query) {
        return
      }

      this.response = response
    },

    /**
     * Callback from Shopify Markets price data is ready.
     * - Update existing products data with new prices by matching ID.
     * - If no localised product or prices then reveal price.
     * - Better to show an incorrect price than a loading state.
     * @param {Array} prices - Localised price data.
     */
    boostPricesUpdated(prices) {
      if (!prices.length) {
        this.products = this.products.map((product) => {
          return {
            ...product,
            price: {
              ...product.price,
              loading: false,
            },
          }
        })

        return
      }

      this.products = this.products.map((product) => {
        const localisedProduct = prices.find((localProduct) => {
          return localProduct.id === product.id
        })

        if (!localisedProduct) {
          return {
            ...product,
            price: {
              ...product.price,
              loading: false,
            },
          }
        }

        return {
          ...product,
          price: localisedProduct.price,
        }
      })
    },

    /**
     * Update focus trap.
     */
    async updateFocusTrap() {
      await new Promise((resolve) => {
        nextTick().then(resolve)
      })

      if (!this.isActive || !this.query) {
        this.clearFocusTrap()
        return
      }

      this.setFocusTrap({
        container: [
          ...this.searchBars,
          this.$refs.predictiveSearch,
        ],
      })
    },

    /**
     * Handle close click.
     */
    handleCloseClick() {
      this.$emit('close-search')
    },

    /**
     * Handle product click to submit analytics event.
     * @param {Number} id - Product ID.
     * @param {String} url - Product URL.
     */
    handleProductClick(id, url) {
      this.analyticsEvent = {
        pid: String(id),
        u: 'view_product',
        url,
      }
    },

    /**
     * Handle analytics submitted event.
     * - Means submission is complete and we can continue with URL navigation.
     * @param {String} url - URL to navigate to.
     */
    handleAnalyticsSubmitted(url) {
      location.href = url
    },
  },
}
</script>
